import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export const NOTICE_EVENT_IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';
export const NOTICE_EVENT_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

export function validateNoticeEventImage(file) {
    if (!file) return '';
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) return 'JPEG, PNG, GIF, WebP 이미지만 첨부할 수 있습니다.';
    if (file.size > NOTICE_EVENT_IMAGE_MAX_BYTES) return '이미지는 10MB 이하만 첨부할 수 있습니다.';
    return '';
}

export function buildNoticeEventFormData({
    title,
    content,
    id,
    division,
    sendPush,
    imageFile,
    removeImage = false
}) {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('content', content);
    formData.append('id', id);
    if (division !== undefined && division !== null) formData.append('division', String(division));
    if (sendPush !== undefined) formData.append('send_push', String(sendPush));
    if (imageFile) formData.append('image', imageFile);
    if (removeImage) formData.append('remove_image', 'true');
    return formData;
}

function NoticeEventImageField({ imageFile, existingImageUrl = null, removeImage = false, onChange }) {
    const inputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!imageFile) {
            if (inputRef.current) inputRef.current.value = '';
            setPreviewUrl(null);
            return undefined;
        }
        const objectUrl = URL.createObjectURL(imageFile);
        setPreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [imageFile]);

    const visibleImageUrl = previewUrl || (!removeImage ? existingImageUrl : null);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0] || null;
        const validationError = validateNoticeEventImage(file);
        setError(validationError);
        if (validationError) {
            event.target.value = '';
            return;
        }
        onChange({ imageFile: file, removeImage: false });
    };

    const handleRemove = () => {
        setError('');
        if (inputRef.current) inputRef.current.value = '';
        onChange({ imageFile: null, removeImage: Boolean(existingImageUrl) });
    };

    return (
        <FieldGroup>
            <FieldLabel htmlFor="notice-event-image">이미지 첨부 (선택)</FieldLabel>
            <HelpText>JPEG, PNG, GIF, WebP · 최대 10MB</HelpText>
            <FileInput
                ref={inputRef}
                id="notice-event-image"
                type="file"
                accept={NOTICE_EVENT_IMAGE_ACCEPT}
                onChange={handleFileChange}
            />
            {error && <ErrorText role="alert">{error}</ErrorText>}
            {visibleImageUrl && (
                <PreviewBox>
                    <PreviewImage src={visibleImageUrl} alt="첨부 이미지 미리보기" />
                    <RemoveButton type="button" onClick={handleRemove}>이미지 제거</RemoveButton>
                </PreviewBox>
            )}
            {!visibleImageUrl && removeImage && <RemovedText>저장하면 기존 이미지가 제거됩니다.</RemovedText>}
        </FieldGroup>
    );
}

const FieldGroup = styled.div`
    margin-top: 20px;
`;

const FieldLabel = styled.label`
    display: block;
    margin-bottom: 6px;
    color: #555;
    font-weight: 600;
`;

const HelpText = styled.p`
    margin: 0 0 10px;
    color: #777;
    font-size: 0.85rem;
`;

const FileInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: #fff;
`;

const PreviewBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-top: 14px;
`;

const PreviewImage = styled.img`
    display: block;
    max-width: 100%;
    max-height: 320px;
    object-fit: contain;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
`;

const RemoveButton = styled.button`
    padding: 7px 12px;
    border: 1px solid #dc3545;
    border-radius: 6px;
    background: #fff;
    color: #dc3545;
    cursor: pointer;
`;

const ErrorText = styled.p`
    margin: 8px 0 0;
    color: #dc3545;
    font-size: 0.9rem;
`;

const RemovedText = styled.p`
    margin: 10px 0 0;
    color: #8a5a00;
    font-size: 0.9rem;
`;

export default NoticeEventImageField;
